import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { XCircle, AlertTriangle, RefreshCw, MessageCircle } from 'lucide-react';

const SUPPORT_WHATSAPP = import.meta.env.VITE_SUPPORT_WHATSAPP;

export default function PaymentFailure() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('payment_id');
  const status = searchParams.get('status');
  const [mercadoPagoLink, setMercadoPagoLink] = useState<string | null>(null);

  useEffect(() => {
    // Log para analytics/tracking
    console.log('Payment Failure:', { paymentId, status });

    // Recuperar o link do Mercado Pago do localStorage
    const storedLink = localStorage.getItem('mercadopago_init_point');
    if (storedLink) {
      setMercadoPagoLink(storedLink);
    }
  }, [paymentId, status]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF7F2] to-[#DED1BD]/30 dark:from-[#0f0606] dark:to-[#240404] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto mb-4 w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <XCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-red-900 dark:text-red-100">
            Pagamento Não Realizado
          </CardTitle>
          <CardDescription className="text-base mt-2">
            Não foi possível processar seu pagamento
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {paymentId && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">ID da Tentativa</p>
              <p className="text-sm font-mono font-medium text-gray-900 dark:text-gray-100">
                {paymentId}
              </p>
            </div>
          )}

          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900 dark:text-red-100 mb-1">
                  O que pode ter acontecido?
                </p>
                <ul className="text-sm text-red-700 dark:text-red-300 space-y-1 list-disc list-inside">
                  <li>Dados do cartão incorretos</li>
                  <li>Limite insuficiente</li>
                  <li>Pagamento cancelado pelo usuário</li>
                  <li>Problema com a operadora do cartão</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-[#DED1BD]/30 dark:bg-[#B05D41]/10 border border-[#B05D41]/30 dark:border-[#B05D41]/30 rounded-lg p-4">
            <p className="text-sm text-[#240404] dark:text-[#DED1BD] mb-2 font-medium">
              Você pode:
            </p>
            <ul className="text-sm text-[#240404]/80 dark:text-[#DED1BD]/80 space-y-2 list-disc list-inside">
              <li>Tentar novamente com outro cartão</li>
              <li>Verificar os dados do cartão</li>
              <li>Entrar em contato com seu banco</li>
              <li>Aguardar alguns minutos e tentar novamente</li>
            </ul>
          </div>

          <div className="space-y-3 pt-2">
            <Button
              onClick={() => {
                if (mercadoPagoLink) {
                  // Se temos o link do Mercado Pago, redirecionar diretamente
                  window.location.href = mercadoPagoLink;
                } else {
                  // Caso contrário, voltar para a página de pagamento
                  navigate('/payment');
                }
              }}
              className="w-full"
              size="lg"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Tentar Novamente
            </Button>
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
            <p className="text-xs text-center text-gray-600 dark:text-gray-400">
              Precisa de ajuda? Entre em contato com nosso suporte
            </p>
            {SUPPORT_WHATSAPP && (
              <Button
                onClick={() => {
                  const phone = SUPPORT_WHATSAPP.replace(/\D/g, '');
                  const message = encodeURIComponent(
                    `Olá! Tive um problema ao processar meu pagamento (ID: ${paymentId || 'não informado'}) e preciso de ajuda para concluir a compra.`
                  );
                  window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                }}
                variant="outline"
                className="w-full"
                size="sm"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                Falar com Suporte
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
